import { useQuery } from '@apollo/client/react'

import { useSplits } from '@buffer-mono/features'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

import { EXCLUDED_ROLLOUT_STATUSES } from '../constants'

export const GetAlphaFeatures = graphql(/* GraphQL */ `
  query getAlphaFeatures {
    alphaFeatures {
      name
      description
      feedbackUrl
      platforms
      splitName
      rolloutStatus {
        id
        name
      }
    }
  }
`)

export type AlphaFeature = NonNullable<
  ResultOf<typeof GetAlphaFeatures>['alphaFeatures']
>[number]

type GetAlphaFeatures = {
  alphaFeatures: AlphaFeature[] | null
  splits: Record<string, SplitIO.TreatmentWithConfig>
  isLoading: boolean
  hasError: boolean
}

/**
 * Custom hook to query the alpha features
 * @returns {GetAlphaFeatures} The accounts alpha features
 */
export const useAlphaFeatures = (): GetAlphaFeatures => {
  const { data, loading, error } = useQuery(GetAlphaFeatures)

  const names = data?.alphaFeatures?.map((feature) => feature.splitName) ?? []
  const { splits } = useSplits(names)

  if (!data?.alphaFeatures && !loading && !error) {
    return {
      alphaFeatures: [],
      splits: {},
      isLoading: false,
      hasError: true,
    }
  }

  // Handle GraphQL errors if any
  if (error) {
    const unexpectedError = new Error(
      error?.message || 'Unexpected API response',
    )
    logError(unexpectedError, { metaData: { data, error } })
    return {
      alphaFeatures: [],
      splits: {},
      isLoading: false,
      hasError: true,
    }
  }

  // only return web features, so filter any that are mobile, ios, or android
  const webFeatures: AlphaFeature[] =
    data?.alphaFeatures?.filter(
      (feature: AlphaFeature) =>
        !feature.platforms.includes('mobile') &&
        !feature.platforms.includes('ios') &&
        !feature.platforms.includes('android'),
    ) ?? []

  // Filter out features with excluded rollout statuses
  const featuresWithoutExcludedStatuses = webFeatures.filter(
    (feature: AlphaFeature) => {
      if (
        feature.rolloutStatus &&
        EXCLUDED_ROLLOUT_STATUSES.includes(
          feature.rolloutStatus.name.toLowerCase(),
        )
      ) {
        return false
      }
      return true
    },
  )

  // Filter out features with 'control' or 'hidden' treatment
  const visibleFeatures =
    featuresWithoutExcludedStatuses?.filter((feature: AlphaFeature) => {
      const treatment = splits[feature.splitName]?.treatment
      return treatment !== 'control' && treatment !== 'hidden'
    }) ?? []

  return {
    alphaFeatures: visibleFeatures,
    splits,
    isLoading: loading,
    hasError: !!error,
  }
}
